import { HttpClient, HttpContext, HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { GeneralResponse, GeneralResponseFull } from '../models/response.model';
import { HttpHeaders } from '@angular/common/http';
import { SkipLoading } from '../interceptors/loading.interceptor';


/**
 *  CRUD
 */
export abstract class CrudService {
    /**
     * Constructor
     */
    constructor(
        protected http: HttpClient,
    ) {}

    skipLoading(){
        return new HttpContext().set(SkipLoading, true);
    }
    
    /**
     * Get
     */
    protected async get<T>( url = '', params?: any, notLoading?: boolean): Promise<T> {
        try {
            let result: GeneralResponse<T>|null = null;
            if (notLoading) {
                result = await firstValueFrom(this.http.get<GeneralResponse<T>>(url, { params: params ? params : undefined, context: this.skipLoading() }));
            } else {
                result = await firstValueFrom(this.http.get<GeneralResponse<T>>(url, { params: params ? params : undefined }));
            }
            return result?.response ? result.response : null;
        } catch (error) {
            this.errorHandler("GET", error)  
            return null;
        }        
    }

    protected async post<T>( url = '', body?: any , params?: any, notLoading?: boolean): Promise<T> {        
        try {
            let result: GeneralResponse<T>|null = null;
            if (notLoading) {
                result = await firstValueFrom(this.http.post<GeneralResponse<T>>(url, body, { params: params ? params : {}, context: this.skipLoading() }));
            } else {
                result = await firstValueFrom(this.http.post<GeneralResponse<T>>(url, body, { params: params ? params : {} }));
            }
            return result?.response ? result.response : null;
        } catch (error) {
            this.errorHandler("POST", error)
            return null;
        }
    }

    protected async uploadFile<T>( url = '', formdata: FormData, notLoading?: boolean ): Promise<T> {        
        try {
            const headers = new HttpHeaders({ 
                'enctype': 'multipart/form-data', 
            });
            let result: GeneralResponse<T>|null = null;
            if (notLoading) {
                result = await firstValueFrom(this.http.post<GeneralResponse<T>>(url, formdata, { headers: headers, context: this.skipLoading() }));
            } else {
                result = await firstValueFrom(this.http.post<GeneralResponse<T>>(url, formdata, { headers: headers }));
            }
            return result?.response ? result.response : null;
        } catch (error) {
            this.errorHandler("POST", error)
            return null;
        }
    }

    protected async put<T>( url = '', body?: any, notLoading?: boolean ): Promise<T|null> {        
        try {
            let result: GeneralResponse<T>|null = null;
            if (notLoading) {
                result = await firstValueFrom(this.http.put<GeneralResponse<T>>(url, body, { context: this.skipLoading() }));
            } else {
                result = await firstValueFrom(this.http.put<GeneralResponse<T>>(url, body));
            }
            return result?.response ? result.response : null;
        } catch (error) {
            this.errorHandler("PUT", error)
            return null;
        }
    }

    protected async patch<T>( url = '', body?: any, notLoading?: boolean ): Promise<T|null> {        
        try {
            let result: GeneralResponse<T>|null = null;
            if (notLoading) {
                result = await firstValueFrom(this.http.patch<GeneralResponse<T>>(url, body, { context: this.skipLoading() }));
            } else {
                result = await firstValueFrom(this.http.patch<GeneralResponse<T>>(url, body));
            }
            return result?.response ? result.response : null;    
        } catch (error) {
            this.errorHandler("PATCH", error)
            return null;
        }
    }

    protected async patchFull( url = '', body?: any, notLoading?: boolean ): Promise<GeneralResponseFull|null> {        
        try {
            let result: GeneralResponseFull = null;
            if (notLoading) {
                result = await firstValueFrom(this.http.patch<GeneralResponseFull>(url, body, { context: this.skipLoading() }));
            } else {
                result = await firstValueFrom(this.http.patch<GeneralResponseFull>(url, body));
            }
            return result;    
        } catch (error) {
            this.errorHandler("PATCH", error)
            return null;
        }
    }

    protected async delete<T>( url = '', notLoading?: boolean): Promise<T|null> {        
        try {
            let result: GeneralResponse<T>|null = null;
            if (notLoading) {
                result = await firstValueFrom(this.http.delete<GeneralResponse<T>>(url, { context: this.skipLoading() }));
            } else {
                result = await firstValueFrom(this.http.delete<GeneralResponse<T>>(url));
            }
            return result?.response ? result.response : null;
        } catch (error) {
            this.errorHandler("DELETE", error)
            return null;
        }
    }

    protected async downloadFile( url = '', notLoading?: boolean) {        
        try {
            let result$;
            if (notLoading) {
                result$ = this.http.get(url, { observe: 'response', responseType: 'blob', context: this.skipLoading() });
            } else {
                result$ = this.http.get(url, { observe: 'response', responseType: 'blob'  });
            }
            result$.subscribe( (response: HttpResponse<Blob>) => {
                let contentDisposition = response.headers.get('Content-Disposition');                
                let filename = contentDisposition?.split('filename=')[1].replace(/"/g, '').trim();
                const downloadURL = window.URL.createObjectURL(response.body);
                const link = document.createElement('a');
                link.href = downloadURL;
                link.download = filename || 'dwonloadfile';
                link.click();
            });
        } catch (error) {
            this.errorHandler("GET DOWNLOAD", error)  
        }        
    }


    protected async postDownloadFile( url = '', body?: any , notLoading?: boolean) {        
        try {
            let result$;
            if (notLoading) {
                result$ = this.http.post(url, { ...body, observe: 'response', responseType: 'blob', context: this.skipLoading() });
            } else {
                result$ = this.http.post(url, { ...body, observe: 'response', responseType: 'blob' });
            }
            result$.subscribe( (response: HttpResponse<Blob>) => {
                let contentDisposition = response.headers.get('Content-Disposition');                
                let filename = contentDisposition?.split('filename=')[1].replace(/"/g, '').trim();
                const downloadURL = window.URL.createObjectURL(response.body);
                const link = document.createElement('a');
                link.href = downloadURL;
                link.download = filename || 'dwonloadfile';
                link.click();
            });
        } catch (error) {
            this.errorHandler("GET DOWNLOAD", error)  
        }  
    }
    
    private errorHandler(method: string, httpError: HttpErrorResponse) {
        console.error(`Error ${method} : `, httpError.error);
        throw new CustomError(httpError.error);
    }
}


export const GENERIC_EXCEPTION_CODES = {
    SUCCESS: "200",
    GENERIC_ERROR: "ERROR000",
    NOT_DATA_FOUND: "ERR001",
    SQL_CONSTRAIN_VIOLATION: "ERRSQL001",
    SQL_CONSTRAIN_DUPLICATE: "ERRSQL002",
    HOLYDAY_DAY_ERROR: "VL0040",
    FORMAT_ERROR_UPLOAD_FILE: "ERROR003",
    DUPLICATE_PROPERTY: "VL0015",
    RESOURCE_IN_USE: "VL0018",
    UNSUCCESS: "300",
    MODULE_REPEAT: "ERROR004",
}

export class CustomError extends Error {
    public error: GeneralResponse<void>;

    constructor(error) {
        super(error.message);
        this.error = error;
    }
}